// src/components/SummaryContainer.js

import React, { useEffect, useState } from 'react';

function SummaryContainer({
	params,
	doorCosts = [], // Provide a default empty array
	removeDoor,
	validateDoorTypes,
	openModal,
	context, // 'estimate' or 'quote'
	navigateToQuoteBuilder,
	currentStep,
	setCurrentStep,
	handleNextStep,
}) {
	const [totals, setTotals] = useState([]);
	const [summaryTotals, setSummaryTotals] = useState({
		totalCost: 0,
		retailTotal: 0,
		totalSavings: 0,
	});

	useEffect(() => {
		const processTotals = () => {
			const doorTotals = params.doors.map((door) => {
				const costData = doorCosts.find((dc) => dc.id === door.id) || {};
				return {
					id: door.id,
					totalCost: costData.totalCost || 0,
					retailCost: costData.retailCost || 0,
					savings: costData.savings || 0,
					displayExteriorColor:
						door.exteriorColorSame === 'Yes' ? 'Same as Interior' : door.exteriorColor,
					doorType: door.doorType,
					widthFt: door.widthFt,
					widthIn: door.widthIn,
					heightFt: door.heightFt,
					heightIn: door.heightIn,
					slideDirection: door.slideDirection,
					openingDirection: door.openingDirection,
					pocketDoor: door.pocketDoor,
					screen: door.screen,
					interiorColor: door.interiorColor,
					exteriorColorSame: door.exteriorColorSame,
					exteriorColor: door.exteriorColor,
					hardwareColor: door.hardwareColor,
				};
			});

			const totalCosts = doorTotals.reduce(
				(sum, current) => ({
					totalCost: sum.totalCost + current.totalCost,
					retailTotal: sum.retailTotal + current.retailCost,
					totalSavings: sum.totalSavings + current.savings,
				}),
				{ totalCost: 0, retailTotal: 0, totalSavings: 0 }
			);

			setTotals(doorTotals);
			setSummaryTotals(totalCosts);
		};

		processTotals();
	}, [params.doors, doorCosts]);

	const hasInvalidDoors =
		params.doors.some(
			(door) =>
				!door.doorType ||
				door.doorType === '' ||
				(Number(door.widthFt || 0) + Number(door.widthIn || 0) === 0) ||
				(Number(door.heightFt || 0) + Number(door.heightIn || 0) === 0)
		) || params.doors.length === 0;

	// Utility function to format numbers as currency with commas and no decimals
	const formatCurrency = (value) => {
		return value.toLocaleString('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		});
	};
	const isBiFoldInvalid = (door) => {
		return (
			!door.openingDirection ||
			!door.slideDirection || 
			!door.screen ||
			!door.interiorColor || 
			((!door.exteriorColorSame || door.exteriorColorSame === 'No') && !door.exteriorColor) || 
			!door.hardwareColor 
		);
	};
	
	const isMultiSlideInvalid = (door) => {
		if (!door.slideDirection) return true; 
		if (door.slideDirection !== 'Middle') {
			if (!door.pocketDoor) return true; 
		}
		if (!door.screen) return true; 
		if (!door.interiorColor) return true; 
		if (!door.hardwareColor) return true;
		if ((!door.exteriorColorSame || door.exteriorColorSame === 'No') && !door.exteriorColor) return true;
	
		return false;
	};
	
	const useIsNextStepDisabled = (doors) => {
		return (
			doors.some((door) => {
				if (door.doorType === 'Bi-Fold') {
					return isBiFoldInvalid(door);
				} else if (door.doorType === 'Multi-Slide') {
					return isMultiSlideInvalid(door);
				}
				return true; 
			}) || doors.length === 0
		);
	};
	
	// Determine if "Next Step" should be disabled
	const isNextStepDisabled = useIsNextStepDisabled(params.doors);
	return (
		<div className="max-w-full md:max-w-[305px] min-w-[300px] p-0 rounded sticky-summary">
			{/* Door Summaries */}
			{/* <div className="sticky top-0 z-10"> */}
			<div className="">
			{totals.map((doorData, index) => (
				<div key={doorData.id} className="bg-white p-4 rounded-[10px] mb-[40px]">
						<div className=' flex justify-between items-center border-b-[1px] w-[100%] border-b-black pb-[25px] pt-[12px] mb-[32px]'>
							<h3 className="text-[24px] font-semibold text-[#1D1D1D] capitalize">Door {index + 1}</h3>
							{index > 0 && (
							<button
								onClick={() => removeDoor(doorData.id)}
								className="px-[20px] py-[8px] hover:bg-red-50 bg-[#E4DEDA] rounded text-[12px] text-[#1D1D1D] font-medium"
							>
								Remove Door
							</button>
							)}
						</div>
					<div className="space-y-2">
						<div className="text-sm">
							<div className='mb-[32px] font-faktum font-medium text-[16px] leading-[20px] text-[#1D1D1D]'>

								<p className="mb-[8px]">Type: {doorData.doorType || 'Not Selected'}</p>
								<p className="mb-[8px]">
									Dimensions: {doorData.widthFt}' {doorData.widthIn}" × {doorData.heightFt}' {doorData.heightIn}"
								</p>
								{context === 'quote' && doorData.doorType === 'Bi-Fold' && (
									<p className="mb-[8px]">Open Direction: {doorData.openingDirection || 'N/A'}</p>
								)}
								
								{context === 'quote' && (
									<p className="mb-[8px]">Slide Direction: {doorData.slideDirection || 'N/A'}</p>
								)}
								{context === 'quote' &&
									doorData.doorType === 'Multi-Slide' &&
									doorData.slideDirection !== 'Middle' && (
										<p className="mb-[8px]">Pocket Door: {doorData.pocketDoor || 'N/A'}</p>
									)}
								{context === 'quote' && (
									<p className="mb-[8px]">Screen: {doorData.screen || 'N/A'}</p>
								)}
								{context === 'quote' && (
									<p className="mb-[8px]">Interior Color: {doorData.interiorColor || 'N/A'}</p>
								)}
								{context === 'quote' && (
									<p className="mb-[8px]">Exterior Color: {doorData.displayExteriorColor}</p>
								)}
								{context === 'quote' && (
									<p className="">Hardware Color: {doorData.hardwareColor || 'N/A'} </p>
								)}
							</div>
							<div className="font-faktum font-medium text-[16px] leading-[20px] text-[#626262]">
								{/* Conditional Rendering of "Retail" and "Savings" */}
								{context !== 'quote' && (
									<>
										<p className="mb-1 flex justify-between items-center"><span>Retail:</span> <span className='line-through'>{formatCurrency(doorData.retailCost)}</span></p>
										<p className="mb-1 flex justify-between items-center text-[#699357]"><span>Savings:</span> {formatCurrency(doorData.savings)}</p>
									</>
								)}
								{/* Add Retail and Savings for Quote context */}
								{context === 'quote' && (
									<>
										<p className="mb-1 flex justify-between items-center"><span className='text-[#626262]'>Retail:</span> <span className='text-[#B6B6B6] line-through'>{formatCurrency(doorData.retailCost)}</span></p>
										<p className="mb-1 flex justify-between items-center text-[#699357]"><span>Savings:</span> <span>-{formatCurrency(doorData.savings)}</span></p>
									</>
								)}
							</div>

						</div>
						<div className="flex justify-between items-center font-medium font-faktum text-[20px] text-[#1D1D1D]">
							<span className=''>Total:</span>
							<span>${doorData.totalCost.toLocaleString()}</span>
						</div>
						<div
							className="text-right text-xs font-faktum text-[12px] text-[#B6B6B6]"
							style={{ lineHeight: 1, marginTop: '0.25rem' }}
						>
							+ tax
						</div>
					</div>
				</div>
			))}
			</div>

			{/* Summary Totals */}
			<div className="bg-white px-[16px] rounded-[10px] mb-[40px]">
				<div className="grid grid-cols-1 gap-1 py-[32px]">
					{/* Always show Retail and Savings in Summary Totals */}
					<div className="line-through flex justify-between items-center font-faktum text-[16px] font-medium text-[#B6B6B6]">
						<span>Total Retail</span>
						<span>
							${summaryTotals.retailTotal.toLocaleString()}
						</span>
					</div>
					<div className="flex justify-between items-center font-faktum text-[16px] font-medium text-[#699357]">
						<span>Savings:</span>
						<span>
						-${Math.abs(Math.round(summaryTotals.totalSavings)).toLocaleString()}

						</span>
					</div>
					<div className="flex justify-between items-center font-semibold text-[20px] leading-[30px] capitalize text-[#1D1D1D]">
						<span>Total Cost:</span>
						<span>${summaryTotals.totalCost.toLocaleString()}</span>
					</div>

					<div
						className="text-right text-[12px] text-[#B6B6B6]"
						style={{ lineHeight: 1, marginTop: '-0.1rem' }}
					>
						+ tax
					</div>

					{/* Action Buttons */}
					<div className="flex flex-col items-center bg-white">
						{context === 'estimate' ? (
							<>
								<button
									onClick={hasInvalidDoors ? validateDoorTypes : navigateToQuoteBuilder}
									className={`bg-[#699357] mt-[32px] w-full mx-auto text-white text-[24px] py-3 rounded-[5px] flex justify-center items-center ${hasInvalidDoors ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-600'
										} transition-colors `}
								>
									Build Your Quote
									<svg className='ml-[16px]' xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
										<g clip-path="url(#clip0_2416_30405)">
											<path d="M18.9596 5.0415L6.04297 17.9582" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
											<path d="M7.33203 5.0415H18.957V16.6665" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
										</g>
										<defs>
											<clipPath id="clip0_2416_30405">
												<rect width="24" height="24" fill="white" transform="translate(0.5)" />
											</clipPath>
										</defs>
									</svg>
								</button>
								<button
									onClick={hasInvalidDoors ? validateDoorTypes : openModal}
									className="text-[16px] text-[#699357] hover:text-[#557c45] mt-[16px] underline"
								>
									Save Estimate
								</button>
							</>
						) : (
							<>
								<button
									onClick={hasInvalidDoors ? validateDoorTypes : handleNextStep}
									disabled={isNextStepDisabled}
									className={`bg-[#699357] mt-[32px] w-full flex gap-[16px] items-center justify-center text-white text-center text-[24px] py-[8px] pl-[32px] pr-[16px] rounded-[5px] ${isNextStepDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#5c824c]'
										} transition-colors`}

								>
									
									<span className='text-[24px] text-nowrap'>{currentStep === 1 ? 'Continue' : 'Submit Quote'}</span>
									<svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
										<g clip-path="url(#clip0_2416_30405)">
											<path d="M18.9596 5.0415L6.04297 17.9582" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
											<path d="M7.33203 5.0415H18.957V16.6665" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
										</g>
										<defs>
											<clipPath id="clip0_2416_30405">
												<rect width="24" height="24" fill="white" transform="translate(0.5)" />
											</clipPath>
										</defs>
									</svg>
								</button>

								{/* "Edit Doors" Button Only in Quote Builder */}
								{currentStep === 2 && (
									<button
										onClick={() => setCurrentStep(1)}
										className="text-base text-[#699357] hover:text-[#4e703f] mt-4"
									>
										Edit Doors
									</button>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default SummaryContainer;
